import { useEffect, useState } from "react";
import useAxios from "axios-hooks";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";

const statusColorMap = {
  Expired: "#a4262c",
  ExpiringIn7Days: "#a4262c",
  ExpiringIn30Days: "#8a6f00",
  ExpiringIn90Days: "#876400",
  ExpiringInMoreThan90Days: "#107c10",
  InvalidCertificate: "#a4262c"
};

const formatStatus = (status) => {
  if (!status) {
    return "Unknown";
  }

  switch (status) {
    case "ExpiringIn7Days":
      return "Expiring in 7 days";
    case "ExpiringIn30Days":
      return "Expiring in 30 days";
    case "ExpiringIn90Days":
      return "Expiring in 90 days";
    case "ExpiringInMoreThan90Days":
      return "Valid (90+ days)";
    case "InvalidCertificate":
      return "Invalid certificate";
    default:
      return status;
  }
};

const formatUtcDate = (dateValue) => {
  if (!dateValue || dateValue === "0001-01-01T00:00:00") {
    return "N/A";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} UTC`;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const errors = responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError?.errorMessage) {
      return firstError.errorMessage;
    }
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const certificateTableStyle = { backgroundColor: "#ffffff" };
const certificateTableHeaderCellStyle = { backgroundColor: "#f3f2f1" };
const certificateRowPrimaryStyle = { backgroundColor: "#ffffff" };

/**
 * Props:
 *   hidden            — controls visibility
 *   certificate       — the certificate object to delete (or null)
 *   selectedCobrandId — cobrand for API URL
 *   cobrandIdpId      — IdP record id
 *   entityId          — entity id string, used for confirmation text
 *   certificates      — full certificate list (to determine if entity-id confirmation is needed)
 *   onDismiss         — close without action
 *   onDeleted         — called after successful delete
 */
const DeleteCertificateDialog = ({
  hidden,
  certificate,
  selectedCobrandId,
  cobrandIdpId,
  entityId,
  certificates,
  onDismiss,
  onDeleted
}) => {
  const [dialogError, setDialogError] = useState("");
  const [entityIdConfirmationText, setEntityIdConfirmationText] = useState("");

  const [{ loading: deletingCertificate }, deleteCertificate] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/delete-certificate", method: "POST" },
    { manual: true }
  );

  useEffect(() => {
    if (hidden) {
      setDialogError("");
      setEntityIdConfirmationText("");
    }
  }, [hidden]);

  const requiresEntityIdConfirmation = Boolean(
    certificate
    && certificate.status !== "Expired"
    && certificate.status !== "InvalidCertificate"
    && certificates.length < 2
  );

  const canSubmitDelete = !requiresEntityIdConfirmation
    || entityIdConfirmationText.trim().toLowerCase() === (entityId || "").trim().toLowerCase();

  const handleDeleteCertificate = async () => {
    setDialogError("");

    if (!selectedCobrandId || !certificate) {
      setDialogError("Unable to delete certificate. Reload the page and try again.");
      return;
    }

    if (requiresEntityIdConfirmation && !canSubmitDelete) {
      setDialogError("Type the exact Entity Id to confirm this delete action.");
      return;
    }

    try {
      await deleteCertificate({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/delete-certificate`,
        data: {
          cobrandIdpCertificateId: certificate.cobrandIdpCertificateId,
          cobrandIdpId,
          entityIdConfirmationText: requiresEntityIdConfirmation ? entityIdConfirmationText : null
        }
      });

      await onDeleted();
      onDismiss();
    } catch (error) {
      setDialogError(getApiErrorMessage(error, "Unable to delete certificate."));
    }
  };

  return (
    <Dialog
      hidden={hidden}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Delete Certificate",
        subText: "This action removes the selected certificate from this IdP."
      }}
      minWidth={500}
    >
      <Stack tokens={{ childrenGap: 10 }}>
        {dialogError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline>
            {dialogError}
          </MessageBar>
        )}

        <Text variant="small">
          {requiresEntityIdConfirmation
            ? "This is the only active certificate. Type the Entity Id below to confirm deletion."
            : "Confirm you want to delete this certificate. This is typically done for expired certificates or when multiple certificates are present."}
        </Text>

        {certificate && (
          <div className="table-responsive">
            <table className="table table-bordered" style={certificateTableStyle}>
              <thead>
                <tr style={certificateRowPrimaryStyle}>
                  <th style={certificateTableHeaderCellStyle}>Use</th>
                  <th style={certificateTableHeaderCellStyle}>Status</th>
                  <th style={certificateTableHeaderCellStyle}>Expires (UTC)</th>
                  <th style={certificateTableHeaderCellStyle}>Thumbprint</th>
                  <th style={certificateTableHeaderCellStyle}>Subject</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{certificate.use || "Unknown"}</td>
                  <td>
                    <span style={{ color: statusColorMap[certificate.status] || "#323130", fontWeight: 600 }}>
                      {formatStatus(certificate.status)}
                    </span>
                  </td>
                  <td>{formatUtcDate(certificate.expirationDateUtc)}</td>
                  <td>{certificate.thumbprint || "N/A"}</td>
                  <td style={{ wordBreak: "break-word", maxWidth: 420 }}>{certificate.subject || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {requiresEntityIdConfirmation && (
          <TextField
            label={`Type Entity Id to confirm: ${entityId}`}
            value={entityIdConfirmationText}
            onChange={(_, value) => setEntityIdConfirmationText(value || "")}
            autoComplete="off"
            errorMessage={
              entityIdConfirmationText && !canSubmitDelete ? "Entity Id does not match." : ""
            }
          />
        )}
      </Stack>

      <DialogFooter>
        <PrimaryButton
          text={deletingCertificate ? "Deleting..." : "Delete Certificate"}
          onClick={handleDeleteCertificate}
          disabled={deletingCertificate || !canSubmitDelete}
        />
        <DefaultButton text="Cancel" onClick={onDismiss} disabled={deletingCertificate} />
      </DialogFooter>
    </Dialog>
  );
};

export default DeleteCertificateDialog;
